import { Loader2, RotateCcw, Search } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { cn } from '@/lib/cn';
import type { BusinessClientSummary, TransactionLookupInput } from '@/types';
import { ClientSelect } from '@/features/bet-inspection/components/ClientSelect';

/**
 * Look up transactions by any ONE id, scoped to a project. Same compact
 * command-bar language as Bet Inspection — project picker, floated labels,
 * inline fields, an accent Search — so the console's lookup bars read the same
 * everywhere. Scoping to the operator also disambiguates ids that repeat across
 * operators.
 */
export function FilterBar({
  loading,
  onSearch,
  onReset,
  clients,
  clientsLoading,
  clientsError,
}: {
  loading: boolean;
  onSearch: (input: TransactionLookupInput) => void;
  onReset: () => void;
  clients: BusinessClientSummary[];
  clientsLoading: boolean;
  clientsError: boolean;
}) {
  const [psid, setPsid] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [trUniqId, setTrUniqId] = useState('');
  const [cycleUniqId, setCycleUniqId] = useState('');

  const canSearch = Boolean(
    psid.trim() && (transactionId.trim() || trUniqId.trim() || cycleUniqId.trim()),
  );

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const play_service_id = psid.trim();
    if (!play_service_id) return;
    if (transactionId.trim()) onSearch({ transaction_id: transactionId.trim(), play_service_id });
    else if (trUniqId.trim()) onSearch({ tr_uniq_id: trUniqId.trim(), play_service_id });
    else if (cycleUniqId.trim()) onSearch({ cycle_uniq_id: cycleUniqId.trim(), play_service_id });
  };

  const reset = () => {
    setTransactionId('');
    setTrUniqId('');
    setCycleUniqId('');
    onReset();
  };

  return (
    <form onSubmit={submit} className="flex flex-wrap items-center gap-1.5 border-b border-hairline px-4 py-2.5">
      <ClientSelect
        value={psid}
        onChange={setPsid}
        clients={clients}
        loading={clientsLoading}
        error={clientsError}
      />
      <Field label="transaction_id" value={transactionId} onChange={setTransactionId} width="w-[180px]" />
      <Field label="tr_uniq_id" value={trUniqId} onChange={setTrUniqId} width="w-[260px]" />
      <Field label="cycle_uniq_id" value={cycleUniqId} onChange={setCycleUniqId} width="w-[260px]" />

      <button
        type="button"
        onClick={reset}
        title="Clear all fields"
        className="inline-flex items-center gap-1.5 border border-hairline px-2.5 py-1.5 font-condensed text-[11px] font-semibold uppercase tracking-wider text-text-dim hover:border-hairline-strong hover:text-text"
      >
        <RotateCcw size={12} /> Reset
      </button>
      <button
        type="submit"
        disabled={!canSearch || loading}
        className={cn(
          'inline-flex items-center gap-1.5 border border-accent/40 bg-accent/10 px-3 py-1.5',
          'font-condensed text-[11px] font-semibold uppercase tracking-wider text-accent',
          'hover:bg-accent/20 disabled:cursor-not-allowed disabled:opacity-40',
        )}
      >
        {loading ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />}
        Search
      </button>

      <span className="ml-auto hidden text-[11px] text-text-faint xl:block">
        Pick a project, then any one id — a cycle id returns every related transaction.
      </span>
    </form>
  );
}

function Field({
  label,
  value,
  onChange,
  width,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  width: string;
}) {
  return (
    <label className={cn('relative block', width)}>
      <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 font-condensed text-[9px] uppercase tracking-wider text-text-faint">
        {label}
      </span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        spellCheck={false}
        autoComplete="off"
        className={cn(
          'w-full border border-hairline bg-surface-sunken py-1.5 pr-2',
          'font-mono text-[12px] text-text placeholder:text-text-faint',
          'hover:border-hairline-strong focus:border-accent focus:outline-none',
        )}
        style={{ paddingLeft: `${label.length * 5 + 16}px` }}
      />
    </label>
  );
}
